import { styled } from '@linaria/react';
import { Link } from 'react-router';
import { Badge } from '../../../../../ui-library/badge/badge';
import { SettingsCard } from '../../shared/settings/settings-card';
import { getFloraConnectionPath } from '../agent-settings.constants';
import { FloraSettingsSection } from '../agent-settings.type';
import { ConnectionTile } from '../connection-tile';
import { useFloraConnections } from '../use-flora-connections';
import { SettingsSectionShell } from './settings-section-shell';

/**
 * Renders the Connections settings section: one row per app Flora can use,
 * with its status and a link to the app's own page, where it is connected,
 * disconnected, and its tools are turned on or off.
 *
 * @returns {JSX.Element} The Connections section
 */
export function SettingsConnectionsSection() {
  const { items, isLoading, isError } = useFloraConnections();
  const connectedCount = items.filter((item) => item.connected).length;

  return (
    <SettingsSectionShell
      section={FloraSettingsSection.Connections}
      subtitle="Apps Flora can use. Open one to connect it and choose which of its tools Flora can use."
      isLoading={isLoading}
      errorMessage={isError ? 'Failed to load connections' : null}
    >
      <section aria-labelledby="settings-apps">
        <ListHeading>
          <Overline id="settings-apps">Apps</Overline>
          <Count>
            {connectedCount} of {items.length} connected
          </Count>
        </ListHeading>
        <SettingsCard>
          <List>
            {items.map((item) => (
              <Row key={item.id}>
                <ConnectionTile connectionId={item.id} />
                <RowText>
                  <RowTitle>
                    <Name>{item.name}</Name>
                    {item.connected ? (
                      <Badge tone="success" icon="check_circle">
                        Connected
                      </Badge>
                    ) : (
                      <Badge tone="neutral">Not connected</Badge>
                    )}
                  </RowTitle>
                  <Description>{item.description}</Description>
                </RowText>
                {item.connected && item.toolCount !== null && <Meta>{item.toolCount} tools</Meta>}
                <RowAction
                  to={getFloraConnectionPath(item.id)}
                  aria-label={`${item.connected ? 'Manage' : 'Connect'} ${item.name}`}
                >
                  {item.connected ? 'Manage' : 'Connect'}
                </RowAction>
              </Row>
            ))}
          </List>
        </SettingsCard>
      </section>
    </SettingsSectionShell>
  );
}

const ListHeading = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
`;

const Overline = styled.h2`
  margin: 0;
  font-size: var(--font-size-xsmall);
  font-weight: var(--font-weight-semibold);
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--ink-light);
`;

const Count = styled.span`
  font-size: var(--font-size-xsmall);
  color: var(--ink-light);
`;

const List = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
`;

const Row = styled.li`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  padding: 16px 20px;

  & + & {
    border-top: 1px solid var(--sky-light);
  }
`;

const RowText = styled.div`
  flex: 1 1 220px;
  min-width: 0;
`;

const RowTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const Name = styled.span`
  font-size: var(--font-size-regular);
  font-weight: var(--font-weight-semibold);
  color: var(--ink-darkest);
`;

const Description = styled.div`
  margin-top: 2px;
  font-size: var(--font-size-small);
  color: var(--ink-light);
  overflow-wrap: anywhere;
`;

const Meta = styled.span`
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-medium);
  color: var(--ink-base);
  white-space: nowrap;
`;

// A link styled as the ui-library outline button: it navigates, so it is an <a>.
const RowAction = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 36px;
  padding: 0 14px;
  border: 1px solid var(--sky-base);
  border-radius: 8px;
  background: var(--base-white);
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-medium);
  color: var(--ink-dark);
  text-decoration: none;
  white-space: nowrap;

  &:hover {
    border-color: var(--secondary-base);
  }

  &:focus-visible {
    outline: 2px solid var(--secondary-base);
    outline-offset: 2px;
  }
`;
